/**
 * Cross-cutting retry helpers: exponential backoff + jitter and timeouts.
 * No I/O; safe for core/domain.
 */

import { randomUnit01 } from '../secureRandom.ts';

export type RetryOptions = {
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
  /** 0–1; adds randomness to reduce thundering herd. */
  jitterRatio?: number;
  /** When false, the error is rethrown with no further attempts. */
  isRetryable?: (error: unknown) => boolean;
};

/** Resolves after the specified number of milliseconds. */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Calculates the backoff delay in milliseconds for a given attempt, including optional jitter. */
export function computeBackoffDelayMs(attemptIndex: number, opts: RetryOptions): number {
  const raw = Math.min(opts.maxDelayMs, opts.baseDelayMs * 2 ** attemptIndex);
  const jitter = opts.jitterRatio ? raw * opts.jitterRatio * randomUnit01() : 0;
  return Math.round(raw + jitter);
}

/**
 * Runs `operation` up to `maxAttempts` times, waiting between failed attempts.
 * `operation` receives the attempt index (0-based); useful e.g. for cache busting.
 */
export async function withRetry<T>(
  operation: (attemptIndex: number) => Promise<T>,
  options: RetryOptions,
): Promise<T> {
  const isRetryable = options.isRetryable ?? (() => true);
  let lastError: unknown;
  for (let attempt = 0; attempt < options.maxAttempts; attempt++) {
    try {
      return await operation(attempt);
    } catch (e) {
      lastError = e;
      if (!isRetryable(e) || attempt === options.maxAttempts - 1) {
        throw e;
      }
      await sleep(computeBackoffDelayMs(attempt, options));
    }
  }
  throw lastError;
}

/**
 * Limits promise duration; clears the timer on success or failure.
 */
export async function withTimeout<T>(promise: Promise<T>, ms: number, message?: string): Promise<T> {
  let tid: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    tid = setTimeout(() => reject(new Error(message ?? `Tiempo de espera agotado (${ms}ms)`)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(tid);
  }
}
